import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageMeta } from '../hooks/usePageMeta';

const BmRequestPage = () => {
  const navigate = useNavigate();
  
  // Set page meta tags
  usePageMeta({
    title: 'Full-Service Marketing & Monthly Management | BM',
    description: 'Need ongoing marketing support? Request full-service help from BM for monthly ad management, full funnel work, and bigger projects beyond BM-Assist.',
    ogTitle: 'Full-Service Marketing & Monthly Management | BM',
    ogDescription: 'Monthly retainers, recurring ad management, and comprehensive marketing solutions for businesses ready to scale.'
  });

  const [formData, setFormData] = useState({
    form_type: 'BM lead',
    full_name: '',
    email: '',
    phone: '',
    business_name: '',
    service_level: '',
    monthly_budget: '',
    current_stack: '',
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bm-leads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        navigate('/submitted');
      } else {
        alert('Failed to submit. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200 px-4 sm:px-6 lg:px-8 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="text-2xl font-bold" style={{ color: '#1E3A8A' }}>
            BM-Assist.co
          </div>
          <a
            href="/"
            className="text-gray-600 hover:text-blue-900 font-medium flex items-center gap-2"
          >
            ← Back to site
          </a>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4" style={{ color: '#1E3A8A' }}>
            Request full-service help from BM
          </h1>
          <p className="text-xl text-gray-600">
            Use this one if you need monthly management, full funnel work, or bigger builds than BM-Assist does.
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-8">
            <h2 className="text-3xl font-bold mb-2" style={{ color: '#1E3A8A' }}>
              BM intake
            </h2>
            <p className="text-gray-600 mb-8">
              Tell us what level of service you're looking for.
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Form Type (readonly) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Form Type
                </label>
                <input
                  type="text"
                  name="form_type"
                  value={formData.form_type}
                  readOnly
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
                />
              </div>

              {/* Full Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone
                </label>
                <input
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Business Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Business name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="business_name"
                  value={formData.business_name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Service Level */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  What are you looking for? <span className="text-red-500">*</span>
                </label>
                <select
                  name="service_level"
                  value={formData.service_level}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select an option</option>
                  <option value="Monthly ad management">Monthly ad management</option>
                  <option value="Ongoing social/content">Ongoing social/content</option>
                  <option value="Full funnel / multi-channel">Full funnel / multi-channel</option>
                  <option value="Consulting / strategy">Consulting / strategy</option>
                  <option value="Other (describe below)">Other (describe below)</option>
                </select>
              </div>

              {/* Monthly Budget */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Estimated monthly budget <span className="text-red-500">*</span>
                </label>
                <select
                  name="monthly_budget"
                  value={formData.monthly_budget}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select budget range</option>
                  <option value="$1,000 – $2,500">$1,000 – $2,500</option>
                  <option value="$2,500 – $5,000">$2,500 – $5,000</option>
                  <option value="$5,000 – $10,000">$5,000 – $10,000</option>
                  <option value="Over $10,000">Over $10,000</option>
                </select>
              </div>

              {/* Current Stack */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current platforms (FB, IG, Google, CRM, etc.)
                </label>
                <input
                  type="text"
                  name="current_stack"
                  value={formData.current_stack}
                  onChange={handleChange}
                  placeholder="e.g., Facebook, Instagram, Google Ads, Mailchimp"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Anything we should know?
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows="4"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 text-lg font-semibold rounded-lg transition-all duration-200 bg-blue-900 text-white hover:bg-blue-800 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Submitting...' : 'Submit to BM'}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 lg:px-8 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto text-center">
          <div className="text-xl font-bold mb-2" style={{ color: '#1E3A8A' }}>
            BM-Assist.co
          </div>
          <p className="text-sm text-gray-600">
            BM-Assist — supported by the BM network.
          </p>
          <div className="mt-6 pt-6 border-t border-gray-300">
            <p className="text-sm text-gray-600">
              © 2025 BM-Assist.co | All rights reserved |{' '}
              <a href="/privacy" className="text-blue-600 hover:underline">Privacy Policy</a> |{' '}
              <a href="/terms" className="text-blue-600 hover:underline">Terms of Service</a> |{' '}
              <a href="/cookies" className="text-blue-600 hover:underline">Cookie Policy</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BmRequestPage;
